import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { ExtendedRequirementTableItemData } from "../../../types";
import { getIsGeneralRequirement } from "../../../utils";
import { useGetPayoutMethodCustomRequirement } from "./useGetPayoutMethodCustomRequirement";
import { useGetRegistrationCustomRequirement } from "./useGetRegistrationCustomRequirement";
import { useGetTaxVerificationCustomRequirement } from "./useGetTaxVerificationCustomRequirement";

type GeneralRequirements = Partial<
  Record<RequirementType, IRequirementResponse[]>
>;

export function usePayerRequirements(requirements: IRequirementResponse[]) {
  const getTaxVerificationCustomRequirement =
    useGetTaxVerificationCustomRequirement();

  const getPayoutMethodCustomRequirement =
    useGetPayoutMethodCustomRequirement();

  const getRegistrationCustomRequirement =
    useGetRegistrationCustomRequirement();

  const clientsRequirements = useMemo(() => {
    // Filter out general requirements
    const normalizedList: ExtendedRequirementTableItemData[] =
      requirements.filter(requirement => !getIsGeneralRequirement(requirement));

    // Group up general requirements
    const generalRequirements = groupGeneralRequirements(requirements);

    (Object.keys(generalRequirements) as RequirementType[]).forEach(key => {
      switch (key) {
        case RequirementType.Registration:
          normalizedList.unshift(
            getRegistrationCustomRequirement(generalRequirements[key]!)
          );
          break;

        case RequirementType.TaxVerification:
          normalizedList.unshift(
            getTaxVerificationCustomRequirement(generalRequirements[key]!)
          );
          break;

        case RequirementType.PayoutMethod:
          normalizedList.unshift(
            getPayoutMethodCustomRequirement(generalRequirements[key]!)
          );
          break;
      }
    });

    return normalizedList;
  }, [
    requirements,
    getTaxVerificationCustomRequirement,
    getPayoutMethodCustomRequirement,
    getRegistrationCustomRequirement
  ]);

  return clientsRequirements;
}

function groupGeneralRequirements(requirements: IRequirementResponse[]) {
  const generalRequirementMap: GeneralRequirements = {};

  requirements.forEach(requirement => {
    if (!getIsGeneralRequirement(requirement)) {
      return;
    }

    const requirementType = requirement.requirementType;
    if (!generalRequirementMap[requirementType]) {
      generalRequirementMap[requirementType] = [];
    }
    generalRequirementMap[requirementType]!.push(requirement);
  });

  return generalRequirementMap;
}
